import { useState } from 'react';

export interface FutureSelfStory {
  hookTitle: string;
  hookSubtitle: string;
  beats: {
    spark: string;
    firstSteps: string;
    challenge: string;
    turningPoint: string;
    now: string;
  };
  impact: string;
  why_math_matters: string;
  generatedImageUrl?: string;
}

interface FutureSelfStoriesProps {
  stories: FutureSelfStory[];
  isGenerating: boolean;
  generationError: string;
  selectedStoryIndex: number | null;
  onSelectStory: (index: number) => void;
  onStoryNext: () => void;
  onComplete: () => void;
  isCompleting?: boolean;
}

interface FutureSelfStoryCardProps {
  story: FutureSelfStory;
  isSelected: boolean;
  onSelect: () => void;
  onStoryNext: () => void;
}

const FUTURE_SELF_CARD_HEIGHT_CLASS = 'tw-h-[470px]';
const STORY_GRID_CLASS = 'tw-grid tw-grid-cols-1 tw-gap-3 md:tw-grid-cols-2 lg:tw-grid-cols-3 xl:tw-grid-cols-4';
const CARD_BASE_CLASS = `tw-rounded-xl tw-border-2 tw-bg-white tw-p-3 tw-space-y-2 tw-flex tw-flex-col tw-relative ${FUTURE_SELF_CARD_HEIGHT_CLASS}`;
const STORY_BEAT_LABELS = ['Spark', 'First Steps', 'Challenge', 'Turning Point', 'Now'] as const;

function FutureSelfStorySkeletonCard() {
  return (
    <div className={`${CARD_BASE_CLASS} tw-border-gray-200 tw-animate-pulse`}>
      <div className="tw-space-y-1">
        <div className="tw-h-4 tw-w-2/3 tw-rounded tw-bg-gray-200" />
        <div className="tw-h-3 tw-w-5/6 tw-rounded tw-bg-gray-200" />
      </div>

      <div className="tw-flex tw-justify-center tw-flex-shrink-0">
        <div className="tw-w-48 tw-h-48 tw-rounded-lg tw-bg-gray-200" />
      </div>

      <div className="tw-flex tw-gap-1">
        {Array.from({ length: 6 }).map((_, beatIndex) => (
          <div key={beatIndex} className="tw-h-1 tw-flex-1 tw-rounded tw-bg-gray-200" />
        ))}
      </div>

      <div className="tw-rounded-lg tw-border tw-border-gray-200 tw-bg-gray-50 tw-p-2 tw-flex-grow tw-min-h-0 tw-space-y-2">
        <div className="tw-h-3 tw-w-1/3 tw-rounded tw-bg-gray-200" />
        <div className="tw-h-3 tw-w-full tw-rounded tw-bg-gray-200" />
        <div className="tw-h-3 tw-w-4/5 tw-rounded tw-bg-gray-200" />
        <div className="tw-h-3 tw-w-2/3 tw-rounded tw-bg-gray-200" />
      </div>

      <div className="tw-grid tw-grid-cols-2 tw-gap-2">
        <div className="tw-h-8 tw-rounded-full tw-bg-gray-200" />
        <div className="tw-h-8 tw-rounded-full tw-bg-gray-200" />
      </div>
    </div>
  );
}

function FutureSelfStoryCard({ story, isSelected, onSelect, onStoryNext }: FutureSelfStoryCardProps) {
  const [currentBeat, setCurrentBeat] = useState(0);
  const title = story.hookTitle;
  const subtitle = story.hookSubtitle;

  const beats = [
    { label: STORY_BEAT_LABELS[0], text: story.beats.spark },
    { label: STORY_BEAT_LABELS[1], text: story.beats.firstSteps },
    { label: STORY_BEAT_LABELS[2], text: story.beats.challenge },
    { label: STORY_BEAT_LABELS[3], text: story.beats.turningPoint },
    { label: STORY_BEAT_LABELS[4], text: story.beats.now },
    {
      label: 'Impact',
      text: (
        <div className="tw-space-y-2">
          <p>
            <span className="tw-font-semibold">Impact:</span> {story.impact}
          </p>
          <p>
            <span className="tw-font-semibold">Why math matters:</span> {story.why_math_matters}
          </p>
        </div>
      ),
    },
  ];

  const canGoBack = currentBeat > 0;
  const canGoForward = currentBeat < beats.length - 1;

  return (
    <div
      onClick={onSelect}
      className={`${CARD_BASE_CLASS} tw-cursor-pointer tw-transition-all ${
        isSelected
          ? 'tw-border-blue-600 tw-shadow-lg tw-shadow-blue-200'
          : 'tw-border-gray-200 hover:tw-border-blue-400 hover:tw-shadow-md'
      }`}
    >
      {isSelected && (
        <div className="tw-absolute tw-top-2 tw-right-2 tw-bg-blue-600 tw-rounded-full tw-p-1">
          <svg className="tw-w-4 tw-h-4 tw-text-white" fill="currentColor" viewBox="0 0 20 20">
            <path
              fillRule="evenodd"
              d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
              clipRule="evenodd"
            />
          </svg>
        </div>
      )}
      <div className="tw-text-sm tw-text-gray-800">
        <div className="tw-font-bold">{title}</div>
        <p className="tw-text-xs tw-text-gray-600 tw-line-clamp-2">{subtitle}</p>
      </div>

      <div className="tw-flex tw-flex-col tw-justify-center tw-flex-shrink-0 tw-rounded-lg tw-border tw-border-gray-200 tw-bg-gray-50 tw-p-2 tw-h-48">
        {story.generatedImageUrl ? (
          <img
            src={story.generatedImageUrl}
            alt={`Generated future self image for ${title}`}
            className="tw-w-full tw-h-full tw-object-contain tw-rounded"
          />
        ) : (
          <span className="tw-text-xs tw-text-gray-400 tw-text-center">Generating image...</span>
        )}
      </div>

      <div className="tw-flex tw-gap-1">
        {beats.map((_, beatIndex) => (
          <div
            key={beatIndex}
            className={`tw-h-1 tw-flex-1 tw-rounded ${beatIndex <= currentBeat ? 'tw-bg-blue-600' : 'tw-bg-gray-200'}`}
          />
        ))}
      </div>

      <div className="tw-rounded-lg tw-bg-gray-50 tw-border tw-border-gray-200 tw-p-2 tw-flex-grow tw-min-h-0 tw-overflow-y-auto">
        <p className="tw-text-xs tw-font-semibold tw-text-gray-600 tw-mb-1">{beats[currentBeat].label}</p>
        <div className="tw-text-xs tw-text-gray-800">
          {typeof beats[currentBeat].text === 'string' ? <p>{beats[currentBeat].text}</p> : beats[currentBeat].text}
        </div>
      </div>

      <div className="tw-grid tw-grid-cols-2 tw-gap-2">
        <button
          type="button"
          disabled={!canGoBack}
          onClick={e => {
            e.stopPropagation();
            setCurrentBeat(prev => Math.max(0, prev - 1));
          }}
          className="tw-rounded-full tw-px-3 tw-py-1.5 tw-text-xs tw-bg-gray-200 tw-text-gray-800 disabled:tw-opacity-50 disabled:tw-cursor-not-allowed hover:tw-bg-gray-300"
        >
          Previous
        </button>
        <button
          type="button"
          disabled={!canGoForward}
          onClick={e => {
            e.stopPropagation();
            setCurrentBeat(prev => Math.min(beats.length - 1, prev + 1));
            onStoryNext();
          }}
          className="tw-rounded-full tw-px-3 tw-py-1.5 tw-text-xs tw-bg-blue-600 tw-text-white disabled:tw-opacity-50 disabled:tw-cursor-not-allowed hover:tw-bg-blue-700"
        >
          Next
        </button>
      </div>
    </div>
  );
}

export function FutureSelfStories({
  stories,
  isGenerating,
  generationError,
  selectedStoryIndex,
  onSelectStory,
  onStoryNext,
  onComplete,
  isCompleting = false,
}: FutureSelfStoriesProps) {
  return (
    <div className="tw-h-full tw-flex tw-flex-col tw-p-4">
      <div className="tw-mb-3">
        <h2 className="tw-text-xl tw-font-bold tw-text-gray-800">Select a Future Self Avatar</h2>
      </div>

      {isGenerating && (
        <div className="tw-flex-1 tw-min-h-0 tw-overflow-y-auto tw-mb-3">
          <div className={STORY_GRID_CLASS}>
            {Array.from({ length: 4 }).map((_, index) => (
              <FutureSelfStorySkeletonCard key={`future-self-skeleton-${index}`} />
            ))}
          </div>
        </div>
      )}

      {generationError && (
        <div className="tw-rounded-lg tw-border tw-border-red-200 tw-bg-red-50 tw-p-3 tw-text-sm tw-text-red-700">
          {generationError}
        </div>
      )}

      {!isGenerating && !generationError && stories.length > 0 && (
        <div className="tw-flex-1 tw-min-h-0 tw-overflow-y-auto tw-mb-3">
          <div className={STORY_GRID_CLASS}>
            {stories.map((story, index) => (
              <FutureSelfStoryCard
                key={`${story.hookTitle}-${index}`}
                story={story}
                isSelected={selectedStoryIndex === index}
                onSelect={() => onSelectStory(index)}
                onStoryNext={onStoryNext}
              />
            ))}
          </div>
        </div>
      )}

      <div className="tw-flex tw-justify-end tw-flex-shrink-0 tw-sticky tw-bottom-0 tw-bg-white tw-pt-2 tw-border-t tw-border-gray-200">
        <button
          type="button"
          onClick={onComplete}
          disabled={selectedStoryIndex === null || isCompleting}
          className="tw-rounded-full tw-px-6 tw-py-2 tw-text-sm tw-bg-blue-600 tw-text-white hover:tw-bg-blue-700 disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
        >
          {isCompleting ? 'Saving...' : 'Complete'}
        </button>
      </div>
    </div>
  );
}
